import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getChecklist, subscribeChecklist, saveChecklist, resetChecklist, newChecklistItem, type ChecklistItem } from "../../data/checklist";
import { logAudit } from "../lib/audit";

/** Basic Review Checklist editor tab — id/text(ar/en) per item, reorder, save, reset. */
export function ChecklistTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [items, setItems] = useState<ChecklistItem[]>(() => getChecklist());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => subscribeChecklist(() => { setItems([...getChecklist()]); setDirty(false); }), []);

  const patch = (i: number, p: Partial<ChecklistItem>) => { setItems((list) => list.map((row, idx) => (idx === i ? { ...row, ...p } : row))); setDirty(true); };
  const add = () => { setItems((list) => [...list, { ...newChecklistItem(), id: "item-" + Date.now().toString(36) }]); setDirty(true); };
  const remove = (i: number) => {
    if (confirm(t("حذف هذا البند من قائمة المراجعة؟", "Delete this checklist item?"))) {
      setItems((list) => list.filter((_, idx) => idx !== i));
      setDirty(true);
    }
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    setItems((list) => { const next = [...list]; [next[i], next[j]] = [next[j], next[i]]; return next; });
    setDirty(true);
  };

  async function save() {
    const seen = new Set<string>();
    for (const it of items) {
      if (!it.id.trim()) return toast.error(t("كل بند لازم يكون له معرّف.", "Every item needs an id."));
      if (seen.has(it.id)) return toast.error(t(`معرّف مكرر: ${it.id}`, `Duplicate id: ${it.id}`));
      seen.add(it.id);
      if (!it.ar.trim() || !it.en.trim()) return toast.error(t("املأ النص بالعربية والإنجليزية لكل بند.", "Fill the Arabic and English text for every item."));
    }
    setSaving(true);
    const res = await saveChecklist(items);
    setSaving(false);
    if (res.ok) { setDirty(false); toast.success(t("تم حفظ قائمة المراجعة", "Checklist saved")); logAudit("config_imported", `Checklist saved (${items.length} items)`); }
    else toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
  }
  async function reset() {
    if (!confirm(t("استعادة قائمة المراجعة الافتراضية؟ أي تعديلات مخصصة ستُفقد.", "Restore the default checklist? Custom changes will be lost."))) return;
    const res = await resetChecklist();
    res.ok ? toast.success(t("تمت الاستعادة", "Reset")) : toast.error(t("تعذّر الاستعادة: ", "Reset failed: ") + (res.error || ""));
  }

  return (
    <div className="kd-admin-paths">
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary kd-btn-gold" onClick={add}>+ {t("إضافة بند جديد", "Add New Item")}</button>
        <button className="kd-btn-primary" onClick={save} disabled={!dirty || saving}>{saving ? t("جارٍ الحفظ...", "Saving...") : t("حفظ التغييرات", "Save Changes")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={reset}>⟲ {t("استعادة الافتراضي", "Reset to Defaults")}</button>
        <span className="kd-admin-path-count">
          {t("عدد البنود:", "Total items:")} <strong>{items.length}</strong>
          {dirty && <span className="kd-admin-dirty-dot"> · {t("تغييرات غير محفوظة", "Unsaved changes")}</span>}
        </span>
      </div>
      <p className="kd-admin-hint">
        {t(
          "هذه هي بنود «قائمة المراجعة الأساسية» في الخطوة قبل الأخيرة من رحلة التأهيل، يؤكّد الموظف كل بند كمكتمل أو يؤجّله. الترتيب هنا هو نفس ترتيب ظهورها للموظف.",
          "These are the \"Essential Checklist\" items in the second-to-last onboarding step, the employee confirms each as done or defers it. The order here matches the order shown to the employee.",
        )}
      </p>
      <div className="kd-admin-paths-list">
        {items.map((it, i) => (
          <div className="kd-admin-path-row" key={i}>
            <div className="kd-admin-path-reorder">
              <button className="kd-admin-action-btn" onClick={() => move(i, -1)} disabled={i === 0} title={t("أعلى", "Up")}>↑</button>
              <span className="kd-admin-path-order">{i + 1}</span>
              <button className="kd-admin-action-btn" onClick={() => move(i, 1)} disabled={i === items.length - 1} title={t("أسفل", "Down")}>↓</button>
            </div>
            <div className="kd-admin-path-fields">
              <label>
                <span>{t("المعرّف (ID)", "Identifier (ID)")}</span>
                <input type="text" value={it.id} onChange={(e) => patch(i, { id: e.target.value.trim().replace(/[^a-z0-9-]/gi, "-").toLowerCase() })} placeholder="device, mfa, ..." style={{ fontFamily: "var(--kd-font-en)" }} />
              </label>
              <div className="kd-admin-grid-2">
                <label><span>{t("النص بالعربية", "Text (Arabic)")}</span><input type="text" value={it.ar} onChange={(e) => patch(i, { ar: e.target.value })} dir="rtl" /></label>
                <label><span>{t("النص بالإنجليزية", "Text (English)")}</span><input type="text" value={it.en} onChange={(e) => patch(i, { en: e.target.value })} dir="ltr" /></label>
              </div>
            </div>
            <div className="kd-admin-path-actions">
              <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => remove(i)} title={t("حذف", "Delete")}>✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
